import { DeviceItemDto } from '@common/generated-api/models/device-item-dto';
import { TaskJournalPageDto } from '@common/generated-api/models/task-journal-page-dto';
import { TaskJournalXlsxLocaleForm } from '@common/generated-api/models/task-journal-xlsx-locale-form';
import { PropsOf } from '@shared/types/type.utils';
import { SurfaceTypes } from '../tasks/calendar/calendar-types';

export type DeviceProcModes = TaskJournalPageDto['modeValue'];

export const DeviceProcessingModesWithSurface: { [key in DeviceProcModes]: string } = {
  ALFA_AIR_BACTERICIDAL: $localize`Воздух => Бактерицидный`,
  ALFA_AREA_BACTERICIDAL: $localize`Поверхность => Бактерицидный`,
  ALFA_AREA_COVID19: $localize`Поверхность => COVID-19`,
  ALFA_AREA_SPORICIDAL: $localize`Поверхность => Спороцидный`,
  ALFA_AREA_TUBERCULOCIDALFUNGICIDAL: $localize`Поверхность => Туберкулоцидный и фунгицидный`,
  YANEX_AREA_BACTERIA: $localize`Бактерицидный`,
  YANEX_AREA_CDIFFICILE: $localize`C.Difficile`,
  YANEX_AREA_COVID19: $localize`COVID-19`,
  ALFA_AIR_URGENT: $localize`Экстренный`,
}

export const DeviceProcessingModes: { [key in DeviceProcModes]: string } = {
  YANEX_AREA_COVID19: $localize`COVID-19 - Yanex`,
  YANEX_AREA_BACTERIA: $localize`Бактерицидный`,
  YANEX_AREA_CDIFFICILE: $localize`C.Difficile`,
  ALFA_AIR_BACTERICIDAL: $localize`Бактерицидный`,
  ALFA_AREA_COVID19: $localize`COVID-19 - Альфа`,
  ALFA_AREA_BACTERICIDAL: $localize`Бактерицидный`,
  ALFA_AREA_SPORICIDAL: $localize`Спороцидный`,
  ALFA_AREA_TUBERCULOCIDALFUNGICIDAL: $localize`Туберкулоцидный и фунгицидный`,
  ALFA_AIR_URGENT: $localize`Экстренный`,
}

export const DeviceLogFields: PropsOf<TaskJournalXlsxLocaleForm> = {
  date: $localize`Дата`,
  departmentName: $localize`Отделение`,
  deviceUpdateVersion: $localize`Версия ПО`,
  duration: $localize`Длит`,
  errorCode: $localize`Код ошибки`,
  errorText: $localize`Текст ошибки`,
  additionalData: $localize`Доп. данные`,
  lampNumber: $localize`№ лампы`,
  lampOperatingTime: $localize`Наработка лампы, имп`,
  technicalOperatingTime: $localize`Наработка установки, имп`,
  lampPhotoSensor: $localize`Поток(%)`,
  modeValue: $localize`Режим`,
  num: $localize`№`,
  pduUpdateVersion: $localize`ПДУ`,
  performerName: $localize`Пользователь`,
  roomDistance: $localize`Расст.(м)`,
  roomName: $localize`Помещение`,
  roomVolume: $localize`Объем(м³)`,
  startTime: $localize`Начало`,
  stopTime: $localize`Конец`,
  valueAlfaAirBactericidal: DeviceProcessingModesWithSurface.ALFA_AIR_BACTERICIDAL,
  valueAlfaAirUrgent: DeviceProcessingModesWithSurface.ALFA_AIR_URGENT,
  valueAlfaAreaBactericidal: DeviceProcessingModesWithSurface.ALFA_AREA_BACTERICIDAL,
  valueAlfaAreaCovid19: DeviceProcessingModesWithSurface.ALFA_AREA_COVID19,
  valueAlfaAreaSporicidal: DeviceProcessingModesWithSurface.ALFA_AREA_SPORICIDAL,
  valueAlfaAreaTuberculocidalFungicidal: DeviceProcessingModesWithSurface.ALFA_AREA_TUBERCULOCIDALFUNGICIDAL,
  valueYanexAreaBacteria: DeviceProcessingModesWithSurface.YANEX_AREA_BACTERIA,
  valueYanexAreaCdifficile: DeviceProcessingModesWithSurface.YANEX_AREA_CDIFFICILE,
  valueYanexAreaCovid19: DeviceProcessingModesWithSurface.YANEX_AREA_COVID19,
}

export const DeviceFieldNames: PropsOf<DeviceItemDto> = {
  accessCode: $localize`Код доступа`,
  cityId: $localize`Город (id)`,
  cityName: $localize`Город`,
  clientId: $localize`Госпиталь (id)`,
  clientName: $localize`Госпиталь`,
  countryCode: $localize`Страна`,
  countryId: $localize`Страна (id)`,
  deviceUpdateVersionId: $localize`Версия программы установки (id)`,
  deviceUpdateVersionName: $localize`Версия программы установки`,
  dispatchDateTime: $localize`Дата отгрузки от производителя`,
  distributorId: $localize`Дистрибьютор (id)`,
  distributorName: $localize`Дистрибьютор`,
  exploitDateTime: $localize`Дата ввода в эксплуатацию`,
  id: $localize`ID`,
  lampNumber: $localize`Номер лампы`,
  lampOperatingTime: $localize`Время наработки лампы`,
  lampPhotoSensor: $localize`Номер лампы установки`,
  modelId: $localize`Модель (id)`,
  modelName: $localize`Модель`,
  name: $localize`Наименование`,
  notes: $localize`Заметки`,
  pduUpdateVersionId: $localize`Версия программы ПДУ`,
  pduUpdateVersionName: $localize`Версия программы ПДУ`,
  producerId: $localize`Производитель (id)`,
  producerName: $localize`Производитель`,
  receiveDateTime: $localize`Дата приемки у производителя`,
  serialNumber: $localize`Серийный № установки`,
  stateValue: $localize`Состояние`,
  technicalOperatingTime: $localize`Наработка установки`,
  warrantyRemainMillis: $localize`Гарантия`,
}

export interface IDeviceLimits {
  volume?: number;
  distance?: number
}

export type DeviceModesWithLimits = Partial<{ [key in DeviceProcModes]: IDeviceLimits }>;

export const Alfa09Limits: DeviceModesWithLimits = {
  ALFA_AIR_BACTERICIDAL: { volume: 100 },
  ALFA_AIR_URGENT: { volume: 100 }, // todo check limit, now ALFA_AIR_BACTERICIDAL copied
  ALFA_AREA_BACTERICIDAL: { distance: 4 },
  ALFA_AREA_COVID19: { distance: 2.5 },
  ALFA_AREA_SPORICIDAL: { distance: 3 },
  ALFA_AREA_TUBERCULOCIDALFUNGICIDAL: { distance: 2 },
}

export const Alfa06Limits: DeviceModesWithLimits = {
  ALFA_AIR_BACTERICIDAL: { volume: 300 },
  ALFA_AIR_URGENT: { volume: 300 },
  ALFA_AREA_BACTERICIDAL: { distance: 5 },
  ALFA_AREA_COVID19: { distance: 5 },
  ALFA_AREA_SPORICIDAL: { distance: 5 },
  ALFA_AREA_TUBERCULOCIDALFUNGICIDAL: { distance: 4 },
}

export const Yanex5Limits: DeviceModesWithLimits = {
  YANEX_AREA_BACTERIA: { distance: 3 },
  YANEX_AREA_CDIFFICILE: { distance: 2.5 },
  YANEX_AREA_COVID19: { distance: 3 },
}

export const Yanex2MLimits: DeviceModesWithLimits = {
  YANEX_AREA_BACTERIA: { distance: 5 },
  YANEX_AREA_CDIFFICILE: { distance: 4 },
  YANEX_AREA_COVID19: { distance: 5 },
}

export const DeviceModelList = [
  { id: 1, name: 'Yanex-2M' },
  { id: 2, name: 'Yanex-5' },
  { id: 3, name: 'Альфа-06' },
  { id: 4, name: 'Альфа-09' },
]

export function GetDeviceLimitsByModelId(modelId: number): DeviceModesWithLimits {
  switch (modelId) {
    case 1:
      return Yanex2MLimits;
    case 2:
      return Yanex5Limits;
    case 3:
      return Alfa06Limits;
    case 4:
      return Alfa09Limits;
    default:
      return null;
  }
}

export interface DeviceProcParamsDefaults {
  airSupported: boolean;
  defaultSurface: SurfaceTypes;
  defaultAirModeName: DeviceProcModes;
  defaultAreaModeName: DeviceProcModes;
}

const devicesDefaultProcParams: {[p in keyof any]: DeviceProcParamsDefaults} = {
  yanex2M: {
    airSupported: false,
    defaultSurface: SurfaceTypes.area,
    defaultAirModeName: null,
    defaultAreaModeName: 'YANEX_AREA_BACTERIA',
  },
  yanex5: {
    airSupported: false,
    defaultSurface: SurfaceTypes.area,
    defaultAirModeName: null,
    defaultAreaModeName: 'YANEX_AREA_BACTERIA',
  },
  alfa06: {
    airSupported: true,
    defaultSurface: SurfaceTypes.area,
    defaultAirModeName: 'ALFA_AIR_BACTERICIDAL',
    defaultAreaModeName: 'ALFA_AREA_BACTERICIDAL',
  },
  alfa09: {
    airSupported: true,
    defaultSurface: SurfaceTypes.area,
    defaultAirModeName: 'ALFA_AIR_BACTERICIDAL',
    defaultAreaModeName: 'ALFA_AREA_BACTERICIDAL',
  },
}

export function GetDeviceProcParamsByModelId(modelId: number): DeviceProcParamsDefaults {
  switch (modelId) {
    case 1:
      return devicesDefaultProcParams.yanex2M;
    case 2:
      return devicesDefaultProcParams.yanex5;
    case 3:
      return devicesDefaultProcParams.alfa06;
    case 4:
      return devicesDefaultProcParams.alfa09;
    default:
      return null;
  }
}
